// ==================== MESSAGES PAGE ====================
// WhatsApp-like chat interface for sending and receiving messages

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Send,
  Paperclip,
  Image as ImageIcon,
  FileText,
  Video,
  MapPin,
  MoreVertical,
  Phone,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  X,
  MessageSquare,
  Loader2,
  Layout,
  RefreshCw,
  Plus,
  UserPlus,
  Users,
  ExternalLink,
  Copy,
  Wifi,
  WifiOff,
  ChevronDown,
  Pencil,
  Reply,
} from 'lucide-react';
import { Button, Input, Badge, Dropdown } from '../../../components/ui';
import type { DropdownItem } from '../../../components/ui/Dropdown';
import {
  messagesService,
  type Conversation,
  type Message,
  type MessagesResponse,
  type SendMessageData,
  type MessageType,
} from '../../../services/messages.service';
import { templatesService, type TemplateItem } from '../../../services/templates.service';
import { contactsService, type Contact } from '../../../services/contacts.service';
import type { ContactGroup } from '../../../types';
import ContactFormModal from '../contacts/ContactFormModal';
import { useWebSocket } from '../../../hooks/useWebSocket';
import type { WhatsAppMessagePayload, MessageStatusUpdatePayload } from '../../../services/websocket.service';

// ==================== 24-HOUR WINDOW COUNTDOWN TIMER ====================
/**
 * Meta WhatsApp Business API 24-Hour Customer Service Window
 * 
 * According to Meta's official documentation:
 * - When a user sends a message to a business, it opens a 24-hour customer service window
 * - During this window, businesses can send service/utility messages for FREE
 * - The window RESETS with each new user message (extends for another 24 hours)
 * - Marketing and authentication templates are CHARGED even within the window
 * - After the window expires, only template messages can be sent (billed per message)
 * 
 * This timer shows the remaining time in the current 24-hour window.
 * The timer is based on the timestamp from Meta's webhook (authoritative source).
 */

interface CountdownTimerProps {
  expiresAt: string | null;
}

const calculateTimeRemaining = (expiresAt: string | null): { timeLeft: string; isExpired: boolean; hoursRemaining: number } => {
  if (!expiresAt) {
    return { timeLeft: '', isExpired: true, hoursRemaining: 0 };
  }

  const now = new Date().getTime();
  const expiry = new Date(expiresAt).getTime();
  let diff = expiry - now;

  if (diff <= 0) {
    return { timeLeft: 'Expired', isExpired: true, hoursRemaining: 0 };
  }

  // Cap the remaining time at 24 hours maximum
  // This is a sanity check - the 24-hour window can never exceed 24 hours
  const maxDiff = 24 * 60 * 60 * 1000;
  if (diff > maxDiff) {
    diff = maxDiff;
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  let timeLeft: string;
  if (hours > 0) {
    timeLeft = `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    timeLeft = `${minutes}m ${seconds}s`;
  } else {
    timeLeft = `${seconds}s`;
  }

  return { timeLeft, isExpired: false, hoursRemaining: hours };
};

const CountdownTimer = ({ expiresAt }: CountdownTimerProps) => {
  // Use a counter to force re-renders every second
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!expiresAt) return;

    // Update every second by incrementing tick
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  // Calculate time remaining on each render (triggered by tick updates)
  const state = calculateTimeRemaining(expiresAt);
  
  // Use tick to prevent unused variable warning
  void tick;

  if (!expiresAt || state.isExpired) return null;

  // Color changes based on urgency: green (>4h), yellow (1-4h), red (<1h)
  const isUrgent = state.hoursRemaining < 1;
  const isWarning = state.hoursRemaining >= 1 && state.hoursRemaining < 4;
  
  const colorClasses = isUrgent 
    ? 'bg-red-500/10 border-red-500/30 text-red-400'
    : isWarning
    ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
    : 'bg-green-500/10 border-green-500/30 text-green-400';

  return (
    <div 
      className={`flex items-center gap-1.5 px-2 py-1 border rounded-full cursor-help ${colorClasses}`}
      title="Free messaging window - service messages are free during this period. Window resets when customer replies."
    >
      <Clock className="w-3 h-3" />
      <span className="text-[10px] font-medium">{state.timeLeft}</span>
    </div>
  );
};

// ==================== DATE SEPARATOR (WhatsApp-style) ====================
/**
 * Formats a date for display as a separator between messages.
 * Shows "Today", "Yesterday", or the full date for older messages.
 */
const formatMessageDate = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date);
  
  // Reset time parts for comparison
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  const messageDateStart = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  
  if (messageDateStart.getTime() === todayStart.getTime()) {
    return 'Today';
  } else if (messageDateStart.getTime() === yesterdayStart.getTime()) {
    return 'Yesterday';
  } else {
    // Check if it's within the current year
    if (messageDate.getFullYear() === today.getFullYear()) {
      return messageDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric' 
      });
    } else {
      return messageDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};

/**
 * Gets the date key for grouping messages (YYYY-MM-DD format)
 */
const getDateKey = (date: Date | string): string => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * WhatsApp-style date separator shown between messages from different days
 */
const DateSeparator = ({ date }: { date: string }) => {
  const formattedDate = formatMessageDate(new Date(date));
  
  return (
    <div className="flex items-center justify-center my-4">
      <div className="px-3 py-1 bg-[#1A2332] rounded-lg shadow-sm">
        <span className="text-xs text-gray-400 font-medium">{formattedDate}</span>
      </div>
    </div>
  );
};

// ==================== MESSAGE STATUS ICON ====================

const MessageStatusIcon = ({ status, direction }: { status: string; direction: string }) => {
  if (direction === 'INBOUND') return null;

  switch (status) {
    case 'PENDING':
      return <Clock className="w-3 h-3 text-gray-400" />;
    case 'SENT':
      return <Check className="w-3 h-3 text-gray-400" />;
    case 'DELIVERED':
      return <CheckCheck className="w-3 h-3 text-gray-400" />;
    case 'READ':
      return <CheckCheck className="w-3 h-3 text-blue-400" />;
    case 'FAILED':
      return <AlertCircle className="w-3 h-3 text-red-400" />;
    default:
      return null;
  }
};

// ==================== TEMPLATE MESSAGE BUBBLE ====================

interface TemplateMessageContentProps {
  content: string;
}

const TemplateMessageContent = ({ content }: TemplateMessageContentProps) => {
  // Parse template content - sections are separated by double newlines
  // Format: [Header]\n\n[Body]\n\n[Footer]
  const sections = content.split('\n\n');
  
  // Try to detect header, body, footer based on content structure
  let header: string | null = null;
  let body: string = content;
  let footer: string | null = null;

  if (sections.length >= 3) {
    // Has header, body, and footer
    header = sections[0];
    body = sections.slice(1, -1).join('\n\n');
    footer = sections[sections.length - 1];
  } else if (sections.length === 2) {
    // Could be header+body or body+footer
    // If last section is short (< 60 chars) and no newlines, it's likely a footer
    if (sections[1].length < 60 && !sections[1].includes('\n')) {
      body = sections[0];
      footer = sections[1];
    } else {
      header = sections[0];
      body = sections[1];
    }
  }

  return (
    <div>
      {/* Template Header */}
      {header && (
        <p className="font-semibold text-gray-900 text-[15px] mb-1">{header}</p>
      )}
      
      {/* Template Body */}
      <p className="text-gray-800 text-[15px] leading-relaxed whitespace-pre-wrap">
        {body}
      </p>
      
      {/* Template Footer */}
      {footer && (
        <p className="text-xs text-gray-500 mt-2">{footer}</p>
      )}
    </div>
  );
};

// ==================== PROXIED MEDIA COMPONENT ====================
// Handles loading media from Meta's authenticated URLs through our proxy

interface ProxiedMediaProps {
  src: string;
  type: 'image' | 'video' | 'audio';
  className?: string;
  alt?: string;
}

const ProxiedMedia = ({ src, type, className = '', alt = '' }: ProxiedMediaProps) => {
  const blobUrlRef = useRef<string | null>(null);
  
  // Determine if proxy is needed - check for Meta URLs or media_id: prefix
  const needsProxy = src.includes('fbsbx.com') || src.includes('whatsapp_business') || src.startsWith('media_id:');
  
  // Store result keyed by src to handle src changes
  const [results, setResults] = useState<Record<string, { blobUrl: string | null; error: boolean }>>({});

  useEffect(() => {
    // If no proxy needed, skip fetching
    if (!needsProxy) {
      return;
    }
    
    // If we already have a result for this src, skip
    if (results[src]) {
      return;
    }

    let cancelled = false;

    messagesService.proxyMedia(src)
      .then((blobUrl) => {
        if (!cancelled) {
          // Clean up previous blob URL if exists
          if (blobUrlRef.current && blobUrlRef.current !== blobUrl) {
            URL.revokeObjectURL(blobUrlRef.current);
          }
          blobUrlRef.current = blobUrl;
          setResults(prev => ({ ...prev, [src]: { blobUrl, error: false } }));
        } else {
          // If cancelled, clean up the fetched blob URL
          URL.revokeObjectURL(blobUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults(prev => ({ ...prev, [src]: { blobUrl: null, error: true } }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [src, needsProxy, results]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  // Derive actual source URL
  const currentResult = results[src];
  const actualSrc = needsProxy ? currentResult?.blobUrl : src;
  const isLoading = needsProxy && !currentResult;
  const hasError = needsProxy && currentResult?.error;

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-800/50 ${className}`} style={{ minHeight: '100px' }}>
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (hasError || (needsProxy && !actualSrc)) {
    return (
      <div className={`flex items-center justify-center bg-gray-800/50 text-gray-400 text-sm ${className}`} style={{ minHeight: '60px' }}>
        <span>Failed to load media</span>
      </div>
    );
  }

  if (!actualSrc) {
    return null;
  }

  if (type === 'image') {
    return (
      <img 
        src={actualSrc} 
        alt={alt} 
        className={className}
        onError={() => setResults(prev => ({ ...prev, [src]: { blobUrl: null, error: true } }))}
      />
    );
  }

  if (type === 'video') {
    return <video src={actualSrc} controls className={className} />;
  }

  if (type === 'audio') {
    return <audio src={actualSrc} controls className={className} />;
  }

  return null;
};

// ==================== DOCUMENT MESSAGE COMPONENT ====================
// Handles document display with download through proxy

interface DocumentMessageProps {
  mediaUrl: string | null | undefined;
  content: string | null | undefined;
  filename?: string | null;
}

const DocumentMessage = ({ mediaUrl, content, filename }: DocumentMessageProps) => {
  const [downloading, setDownloading] = useState(false);

  // Determine display name - prefer filename from metadata, fallback to content if it looks like a filename
  const isContentFilename = content && (content.includes('.') || content === '[Document]');
  const displayFilename = filename || (isContentFilename ? content : null) || 'Document';
  // Caption is content if it's not just a filename placeholder
  const caption = content && !isContentFilename && content !== '[Document]' ? content : null;

  const handleDownload = async () => {
    if (!mediaUrl) return;
    
    setDownloading(true);
    try {
      // Use the proxy to fetch the document
      const blobUrl = await messagesService.proxyMedia(mediaUrl);
      
      // Create a download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mb-2" style={{ minWidth: '200px' }}>
      <div className="flex items-center gap-3 p-3 bg-black/10 rounded-lg">
        <div className="w-11 h-14 bg-red-500 rounded flex items-center justify-center text-white text-xs font-bold shrink-0">
          DOC
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{displayFilename}</p>
          <p className="text-xs opacity-70">Document</p>
          {mediaUrl && (
            <button 
              onClick={handleDownload}
              disabled={downloading}
              className="text-xs text-green-400 hover:underline disabled:opacity-50 mt-1"
            >
              {downloading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>
      </div>
      {caption && (
        <p className="text-[15px] leading-relaxed whitespace-pre-wrap mt-2 px-1">{caption}</p>
      )}
    </div>
  );
};

// ==================== MESSAGE BUBBLE ====================

interface MessageBubbleProps {
  message: Message;
  isNew?: boolean; // Only animate if message is newly added
}

const MessageBubble = ({ message, isNew = false }: MessageBubbleProps) => {
  const isOutbound = message.direction === 'OUTBOUND';
  const time = new Date(message.createdAt).toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  const isTemplate = message.type?.toLowerCase() === 'template';

  // WhatsApp-style colors
  // Outbound: WhatsApp green (#005C4B dark mode / #DCF8C6 light mode)
  // Inbound: WhatsApp gray (#202C33 dark mode / white light mode)
  const bubbleClasses = isOutbound
    ? 'bg-[#005C4B] text-white rounded-tr-lg rounded-tl-lg rounded-bl-lg rounded-br-sm'
    : 'bg-[#202C33] text-white rounded-tr-lg rounded-tl-lg rounded-br-lg rounded-bl-sm';

  // For template messages, use WhatsApp's white bubble style
  const templateBubbleClasses = 'bg-white text-gray-900 rounded-lg shadow-md';

  // Only animate new messages to prevent flickering on initial load
  const animationProps = isNew 
    ? { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } }
    : { initial: false, animate: { opacity: 1, y: 0 } };

  return (
    <motion.div
      {...animationProps}
      transition={{ duration: 0.15 }}
      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3 px-2`}
    >
      <div
        className={`${isTemplate ? templateBubbleClasses : bubbleClasses} overflow-hidden`}
        style={{ maxWidth: '320px', minWidth: '180px' }}
      >
        {/* Template Message - WhatsApp style */}
        {isTemplate && message.content && (
          <div>
            {/* Template Header Media (Image/Video/Document) */}
            {message.mediaUrl && (() => {
              // Use metadata.headerType as primary source, fallback to URL pattern matching
              const headerType = message.metadata?.headerType?.toUpperCase();
              const isImage = headerType === 'IMAGE' || 
                (!headerType && (message.mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) || message.mediaUrl.includes('image')));
              const isVideo = headerType === 'VIDEO' || 
                (!headerType && (message.mediaUrl.match(/\.(mp4|webm|mov)$/i) || message.mediaUrl.includes('video')));
              
              return (
                <div className="w-full">
                  {isImage ? (
                    <ProxiedMedia 
                      src={message.mediaUrl} 
                      type="image"
                      alt="Template Header" 
                      className="w-full max-h-48 object-cover"
                    />
                  ) : isVideo ? (
                    <ProxiedMedia 
                      src={message.mediaUrl} 
                      type="video"
                      className="w-full max-h-48"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-100 border-b border-gray-200">
                      <div className="w-10 h-12 bg-red-500 rounded flex items-center justify-center text-white text-xs font-bold shrink-0">
                        DOC
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {message.mediaUrl.split('/').pop() || 'Document'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            <div className="px-3 py-2">
              <TemplateMessageContent content={message.content} />
              
              {/* Time and Status for template */}
              <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[11px] text-gray-400">{time}</span>
                {isOutbound && <MessageStatusIcon status={message.status} direction={message.direction} />}
              </div>
            </div>
            
            {/* Template Buttons (not interactive buttons) */}
            {message.metadata?.buttons && message.metadata.buttons.length > 0 && !message.metadata?.interactiveType && (
              <div className="border-t border-gray-200">
                {(message.metadata.buttons as Array<{ type?: string; text?: string }>).map((button, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center gap-2 py-2.5 text-[#00A5F4] text-sm font-medium border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                  >
                    {button.type === 'URL' && (
                      <>
                        <ExternalLink className="w-4 h-4" />
                        <span>{button.text}</span>
                      </>
                    )}
                    {button.type === 'PHONE' && (
                      <>
                        <Phone className="w-4 h-4" />
                        <span>{button.text}</span>
                      </>
                    )}
                    {button.type === 'QUICK_REPLY' && (
                      <span>{button.text}</span>
                    )}
                    {button.type === 'COPY_CODE' && (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>{button.text}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Regular Message Content */}
        {!isTemplate && (() => {
          const messageType = message.type?.toLowerCase();
          return (
          <div className="px-3 py-2">
            {messageType === 'image' && message.mediaUrl && (
              <div className="mb-2 -mx-3 -mt-2" style={{ minWidth: '200px' }}>
                <ProxiedMedia 
                  src={message.mediaUrl} 
                  type="image"
                  alt="Image" 
                  className="w-full max-h-64 object-cover"
                />
              </div>
            )}
            {messageType === 'video' && message.mediaUrl && (
              <div className="mb-2 -mx-3 -mt-2" style={{ minWidth: '200px' }}>
                <ProxiedMedia 
                  src={message.mediaUrl} 
                  type="video"
                  className="w-full max-h-64"
                />
              </div>
            )}
            {messageType === 'audio' && message.mediaUrl && (
              <div className="mb-2" style={{ minWidth: '220px' }}>
                <ProxiedMedia 
                  src={message.mediaUrl} 
                  type="audio"
                  className="w-full"
                />
              </div>
            )}
            {messageType === 'document' && (
              <DocumentMessage 
                mediaUrl={message.mediaUrl} 
                content={message.content}
                filename={message.metadata?.filename}
              />
            )}
            {messageType === 'location' && (() => {
              const lat = message.metadata?.latitude;
              const lng = message.metadata?.longitude;
              const locationName = message.metadata?.name;
              const locationAddress = message.metadata?.address;
              const hasCoords = lat !== undefined && lng !== undefined;
              const mapsUrl = hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : null;
              const displayName = locationName || message.content || 'Location';
              
              return (
                <div className="mb-2 -mx-3 -mt-2" style={{ minWidth: '220px' }}>
                  {/* Map Preview - using OpenStreetMap embed as fallback */}
                  {hasCoords && (
                    <a 
                      href={mapsUrl!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative group"
                    >
                      <div className="w-full h-32 bg-[#1a2e1a] overflow-hidden">
                        <iframe
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng! - 0.01},${lat! - 0.01},${lng! + 0.01},${lat! + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                          className="w-full h-full border-0 pointer-events-none"
                          title="Location Map"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-2">
                          <ExternalLink className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </a>
                  )}
                  
                  {/* Location Info */}
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {displayName !== 'Location' ? displayName : 'Location'}
                        </p>
                        {locationAddress && (
                          <p className="text-[11px] opacity-70 truncate">{locationAddress}</p>
                        )}
                        {hasCoords && !locationAddress && (
                          <p className="text-[11px] opacity-60">
                            {lat!.toFixed(6)}, {lng!.toFixed(6)}
                          </p>
                        )}
                      </div>
                    </div>
                    {hasCoords && (
                      <a 
                        href={mapsUrl!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-400 hover:underline mt-1 inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open in Google Maps
                      </a>
                    )}
                  </div>
                </div>
              );
            })()}
            {messageType === 'contacts' && (
              <div className="flex items-center gap-3 p-3 bg-black/10 rounded-lg mb-2" style={{ minWidth: '180px' }}>
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Contact Card</p>
                  {message.content && message.content !== '[contacts]' && (
                    <p className="text-xs opacity-70 truncate">{message.content}</p>
                  )}
                </div>
              </div>
            )}
            {/* Show text content for text messages, or caption for media */}
            {message.content && messageType !== 'document' && messageType !== 'location' && messageType !== 'contacts' && !['[image]', '[video]', '[audio]', '[contacts]'].includes(message.content.toLowerCase()) && (
              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
            )}
            
            {/* Time and Status */}
            <div className="flex items-center justify-end gap-1 mt-1">
              <span className={`text-[11px] ${isOutbound ? 'text-white/60' : 'text-gray-400'}`}>{time}</span>
              {isOutbound && <MessageStatusIcon status={message.status} direction={message.direction} />}
            </div>
          </div>
        );
        })()}

        {/* Interactive Quick Reply Buttons (from bot messages) */}
        {message.metadata?.interactiveType === 'button' && message.metadata?.buttons && Array.isArray(message.metadata.buttons) && message.metadata.buttons.length > 0 && (
          <div className="border-t border-gray-600/30 mt-1">
            {(message.metadata.buttons as unknown as Array<{ id: string; title: string }>).map((button, index) => (
              <div
                key={button.id || index}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium border-b border-gray-600/20 last:border-b-0 ${
                  isOutbound 
                    ? 'text-white/90 hover:bg-white/10' 
                    : 'text-[#00A5F4] hover:bg-gray-50'
                } cursor-default`}
              >
                <Reply className="w-4 h-4 opacity-70" />
                <span>{button.title}</span>
              </div>
            ))}
          </div>
        )}

        {/* Error Message */}
        {message.status === 'FAILED' && message.errorMessage && (
          <div className="px-3 pb-2">
            <p className="text-xs text-red-400">{message.errorMessage}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ==================== CONVERSATION ITEM ====================

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}

const ConversationItem = ({ conversation, isActive, onClick }: ConversationItemProps) => {
  const time = new Date(conversation.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const messageDate = new Date(conversation.lastMessageAt);
  const isToday = messageDate.toDateString() === today.toDateString();
  const displayTime = isToday ? time : messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors border-b border-gray-800/50 ${
        isActive ? 'bg-(--color-orange-100)' : 'hover:bg-[#1A2332]'
      }`}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-(--color-signal-orange) flex items-center justify-center text-white text-sm font-semibold shrink-0">
        {conversation.contactName.charAt(0).toUpperCase()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-medium text-white truncate">{conversation.contactName}</h4>
          <span className="text-[11px] text-gray-400 shrink-0">{displayTime}</span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="text-xs text-gray-400 truncate flex items-center gap-1">
            {conversation.lastMessage.direction === 'OUTBOUND' && (
              <MessageStatusIcon status={conversation.lastMessage.status} direction="OUTBOUND" />
            )}
            {conversation.lastMessage.content}
          </p>
          {conversation.unreadCount > 0 && (
            <Badge variant="orange" className="rounded-full min-w-[18px] h-4 flex items-center justify-center text-[10px]">
              {conversation.unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== TEMPLATE SELECTOR ====================

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: TemplateItem) => void;
}

const TemplateSelector = ({ isOpen, onClose, onSelect }: TemplateSelectorProps) => {
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await templatesService.getAll({ status: 'APPROVED', limit: 100 });
      setTemplates(response.items);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(
    (t) => t.name.toLowerCase().includes(search.toLowerCase()) || t.bodyText?.toLowerCase().includes(search.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="absolute bottom-full left-0 right-0 mb-2 bg-[#121723] border border-gray-800 rounded-xl shadow-xl max-h-[350px] overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-gray-800">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-white">Select Template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <Input
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search className="w-3.5 h-3.5" />}
        />
      </div>
      <div className="max-h-[260px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center p-6">
            <Loader2 className="w-5 h-5 text-(--color-signal-orange) animate-spin" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            <Layout className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No approved templates found</p>
          </div>
        ) : (
          filteredTemplates.map((template, idx) => (
            <div
              key={template.id || `template-${idx}`}
              onClick={() => onSelect(template)}
              className="px-3 py-2 border-b border-gray-800/50 cursor-pointer hover:bg-[#1A2332] transition-colors"
            >
              <div className="flex items-center justify-between mb-0.5">
                <h4 className="text-xs font-medium text-white">{template.name}</h4>
                <Badge variant="success" className="text-[10px]">{template.category}</Badge>
              </div>
              <p className="text-[11px] text-gray-400 line-clamp-2">{template.bodyText}</p>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
};

// ==================== ATTACHMENT MENU ====================

type AttachmentType = 'image' | 'video' | 'document' | 'location';

interface AttachmentMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: AttachmentType) => void;
}

const AttachmentMenu = ({ isOpen, onClose, onSelect }: AttachmentMenuProps) => {
  if (!isOpen) return null;

  const items = [
    { type: 'image' as const, icon: ImageIcon, label: 'Image', color: 'bg-purple-500' },
    { type: 'video' as const, icon: Video, label: 'Video', color: 'bg-pink-500' },
    { type: 'document' as const, icon: FileText, label: 'Document', color: 'bg-blue-500' },
    { type: 'location' as const, icon: MapPin, label: 'Location', color: 'bg-green-500' },
  ];

  return (
    <>
      {/* Invisible overlay to capture clicks outside the menu */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="absolute bottom-full left-0 mb-2 bg-[#121723] border border-gray-800 rounded-xl shadow-xl p-2 z-50"
      >
        <div className="grid grid-cols-2 gap-2">
          {items.map((item) => (
            <button
              key={item.type}
              onClick={() => {
                onSelect(item.type);
                onClose();
              }}
              className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[#1A2332] transition-colors"
            >
              <div className={`w-10 h-10 ${item.color} rounded-full flex items-center justify-center`}>
                <item.icon className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs text-gray-300">{item.label}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </>
  );
};

// ==================== MEDIA INPUT MODAL ====================

interface MediaInputModalProps {
  isOpen: boolean;
  mediaType: AttachmentType | null;
  onClose: () => void;
  onSend: (data: { 
    type: AttachmentType; 
    file?: File;
    caption?: string; 
    location?: { latitude: number; longitude: number; name?: string } 
  }) => void;
  sending: boolean;
  uploadProgress: number;
}

const MediaInputModalContent = ({ mediaType, onClose, onSend, sending, uploadProgress }: Omit<MediaInputModalProps, 'isOpen'> & { mediaType: AttachmentType }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationName, setLocationName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getTypeConfig = () => {
    switch (mediaType) {
      case 'image':
        return { 
          title: 'Send Image', 
          icon: ImageIcon,
          color: 'bg-purple-500',
          hint: 'Supported formats: JPG, PNG, WebP (max 5MB)',
          accept: 'image/jpeg,image/png,image/webp',
          maxSize: 5 * 1024 * 1024,
        };
      case 'video':
        return { 
          title: 'Send Video', 
          icon: Video,
          color: 'bg-pink-500',
          hint: 'Supported format: MP4, 3GPP (max 16MB)',
          accept: 'video/mp4,video/3gpp',
          maxSize: 16 * 1024 * 1024,
        };
      case 'document':
        return { 
          title: 'Send Document', 
          icon: FileText,
          color: 'bg-blue-500',
          hint: 'Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, TXT (max 100MB)',
          accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          maxSize: 100 * 1024 * 1024,
        };
      case 'location':
        return { 
          title: 'Send Location', 
          icon: MapPin,
          color: 'bg-green-500',
          hint: 'Enter coordinates for the location',
          accept: '',
          maxSize: 0,
        };
    }
  };

  const config = getTypeConfig();
  const isLocation = mediaType === 'location';
  
  const canSubmit = isLocation 
    ? latitude.trim() && longitude.trim()
    : selectedFile !== null;

  const handleFileSelect = (file: File) => {
    if (file.size > config.maxSize) {
      alert(`File size exceeds maximum of ${config.maxSize / (1024 * 1024)} MB`);
      return;
    }
    setSelectedFile(file);
    
    // Create preview for images
    if (mediaType === 'image') {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else if (mediaType === 'video') {
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    if (isLocation) {
      onSend({
        type: mediaType,
        location: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          name: locationName.trim() || undefined,
        },
      });
    } else if (selectedFile) {
      onSend({
        type: mediaType,
        file: selectedFile,
        caption: caption.trim() || undefined,
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#121723] border border-gray-800 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 ${config.color} rounded-full flex items-center justify-center`}>
              <config.icon className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-sm font-medium text-white">{config.title}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="p-1 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {isLocation ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Latitude *</label>
                  <Input
                    type="number"
                    step="any"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder="e.g., 24.8607"
                    className="text-sm"
                    disabled={sending}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Longitude *</label>
                  <Input
                    type="number"
                    step="any"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder="e.g., 67.0011"
                    className="text-sm"
                    disabled={sending}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Location Name (optional)</label>
                <Input
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g., Office, Home, etc."
                  className="text-sm"
                  disabled={sending}
                />
              </div>
            </>
          ) : (
            <>
              {/* File Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragOver 
                    ? 'border-orange-500 bg-orange-500/10' 
                    : selectedFile 
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-gray-700 hover:border-gray-600'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => !sending && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={config.accept}
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  disabled={sending}
                />
                
                {selectedFile ? (
                  <div className="space-y-2">
                    {preview && mediaType === 'image' && (
                      <img src={preview} alt="Preview" className="w-full max-h-32 object-contain mx-auto rounded" />
                    )}
                    {preview && mediaType === 'video' && (
                      <video src={preview} className="w-full max-h-32 object-contain mx-auto rounded" controls />
                    )}
                    {mediaType === 'document' && (
                      <div className="w-12 h-14 mx-auto bg-blue-500 rounded flex items-center justify-center">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <p className="text-sm text-white truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(selectedFile.size)}</p>
                    {!sending && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreview(null); }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className={`w-12 h-12 mx-auto ${config.color} rounded-full flex items-center justify-center opacity-50`}>
                      <config.icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-sm text-gray-300">
                      Drag & drop or <span className="text-orange-400">browse</span>
                    </p>
                    <p className="text-[10px] text-gray-500">{config.hint}</p>
                  </div>
                )}
              </div>

              {/* Caption for images/videos/documents */}
              {(mediaType === 'image' || mediaType === 'video' || mediaType === 'document') && selectedFile && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">
                    {mediaType === 'document' ? 'Filename / Caption (optional)' : 'Caption (optional)'}
                  </label>
                  <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder={mediaType === 'document' ? 'Custom filename or caption...' : 'Add a caption...'}
                    className="text-sm"
                    disabled={sending}
                  />
                </div>
              )}

              {/* Upload Progress */}
              {sending && uploadProgress > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-orange-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-800 bg-[#0D1117]">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSubmit}
            disabled={!canSubmit || sending}
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadProgress > 0 && uploadProgress < 100 ? 'Uploading...' : 'Sending...'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Wrapper component to handle conditional rendering and reset state on each open
const MediaInputModal = ({ isOpen, mediaType, onClose, onSend, sending, uploadProgress }: MediaInputModalProps) => {
  if (!isOpen || !mediaType) return null;
  
  // Key changes when mediaType changes, forcing fresh state
  return (
    <MediaInputModalContent 
      key={mediaType} 
      mediaType={mediaType} 
      onClose={onClose} 
      onSend={onSend} 
      sending={sending}
      uploadProgress={uploadProgress}
    />
  );
};

// ==================== NEW CHAT CONTACT SELECTOR ====================

interface ContactSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (contact: Contact) => void;
  existingConversationIds: string[];
}

const CONTACTS_PAGE_SIZE = 20;

const ContactSelector = ({ isOpen, onClose, onSelect, existingConversationIds }: ContactSelectorProps) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountRef = useRef(true);

  // Debounce search input - only when search actually changes (not on initial mount)
  useEffect(() => {
    // Skip the initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setHasMore(true);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  // Fetch contacts when modal opens or search changes
  useEffect(() => {
    if (isOpen) {
      fetchContacts(1, true);
    }
  }, [isOpen, debouncedSearch]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setContacts([]);
      setSearch('');
      setDebouncedSearch('');
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
      isInitialMountRef.current = true; // Reset for next open
    }
  }, [isOpen]);

  const fetchContacts = async (pageNum: number, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await contactsService.getAll({
        limit: CONTACTS_PAGE_SIZE,
        page: pageNum,
        search: debouncedSearch || undefined,
        optedIn: true,
      });

      const newContacts = response.items;
      const total = response.meta?.total || 0;

      if (reset) {
        setContacts(newContacts);
      } else {
        setContacts((prev) => [...prev, ...newContacts]);
      }

      setTotalCount(total);
      setHasMore(newContacts.length === CONTACTS_PAGE_SIZE && contacts.length + newContacts.length < total);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when user scrolls to 80% of the list
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      fetchContacts(page + 1, false);
    }
  }, [loadingMore, hasMore, page]);

  // Separate contacts with existing conversations and new contacts
  const existingContacts = contacts.filter((c) => existingConversationIds.includes(c.id));
  const newContacts = contacts.filter((c) => !existingConversationIds.includes(c.id));

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#121723] border border-gray-800 rounded-xl shadow-2xl w-full max-w-sm max-h-[70vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-(--color-signal-orange)" />
              <h3 className="text-sm font-heading font-semibold text-white">Start New Chat</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-3.5 h-3.5" />}
            autoFocus
          />
          {totalCount > 0 && (
            <p className="text-[10px] text-gray-500 mt-1.5">
              {contacts.length} of {totalCount.toLocaleString()} contacts
            </p>
          )}
        </div>
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="max-h-[320px] overflow-y-auto"
        >
          {loading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="w-5 h-5 text-(--color-signal-orange) animate-spin" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">No contacts found</p>
              <p className="text-[10px] text-gray-500 mt-1">
                {debouncedSearch ? 'Try a different search term' : 'Add contacts first to start chatting'}
              </p>
            </div>
          ) : (
            <>
              {/* New contacts section */}
              {newContacts.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 bg-[#0D1117] text-[10px] font-medium text-gray-500 uppercase tracking-wider sticky top-0">
                    New Conversation
                  </div>
                  {newContacts.map((contact, idx) => (
                    <div
                      key={contact.id || `new-contact-${idx}`}
                      onClick={() => onSelect(contact)}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#1A2332] transition-colors border-b border-gray-800/50"
                    >
                      <div className="w-8 h-8 rounded-full bg-(--color-signal-orange) flex items-center justify-center text-white text-xs font-semibold shrink-0">
                        {(contact.name || contact.phoneNumber).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{contact.name || contact.phoneNumber}</h4>
                        <p className="text-[11px] text-gray-400">{contact.phoneNumber}</p>
                      </div>
                      <span className="text-[10px] font-medium text-(--color-signal-orange) shrink-0">NEW</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Existing conversations section */}
              {existingContacts.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 bg-[#0D1117] text-[10px] font-medium text-gray-500 uppercase tracking-wider sticky top-0">
                    Existing Conversations
                  </div>
                  {existingContacts.map((contact, idx) => (
                    <div
                      key={contact.id || `existing-contact-${idx}`}
                      onClick={() => onSelect(contact)}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#1A2332] transition-colors border-b border-gray-800/50"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                        {(contact.name || contact.phoneNumber).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{contact.name || contact.phoneNumber}</h4>
                        <p className="text-[11px] text-gray-400">{contact.phoneNumber}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex items-center justify-center p-3">
                  <Loader2 className="w-4 h-4 text-(--color-signal-orange) animate-spin" />
                  <span className="text-[10px] text-gray-400 ml-2">Loading more...</span>
                </div>
              )}

              {/* End of list indicator */}
              {!hasMore && contacts.length > 0 && (
                <div className="p-2 text-center">
                  <p className="text-[10px] text-gray-500">All contacts loaded</p>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-3 py-2 border-t border-gray-800 bg-[#0D1117]">
          <p className="text-[10px] text-gray-500 text-center">
            💡 For new conversations, you'll need to send a template message first
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const MessagesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialContactId = searchParams.get('contactId');
  
  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesData, setMessagesData] = useState<MessagesResponse | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [totalConversations, setTotalConversations] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showContactSelector, setShowContactSelector] = useState(false);
  const [newChatContact, setNewChatContact] = useState<{ id: string; name: string; phone: string } | null>(null);
  
  // Attachment/Media state
  const [selectedMediaType, setSelectedMediaType] = useState<AttachmentType | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Template variable input state
  const [templateWithVariables, setTemplateWithVariables] = useState<TemplateItem | null>(null);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string>('');
  
  // Edit contact modal state
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingContactFull, setEditingContactFull] = useState<Contact | null>(null);
  const [contactGroups, setContactGroups] = useState<ContactGroup[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const conversationsContainerRef = useRef<HTMLDivElement>(null);
  const selectedContactRef = useRef<string | null>(null);
  
  // Track recently sent message IDs to avoid duplicates from WebSocket
  // These are messages sent by the user via API that might also arrive via WebSocket
  const recentlySentMessageIds = useRef<Set<string>>(new Set());

  // Keep selectedContactRef in sync with selectedContact
  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);

  // ==================== WEBSOCKET INTEGRATION ====================

  /**
   * Handle incoming WhatsApp messages via WebSocket
   * This transforms the WS payload to match our Message interface and updates state
   */
  const handleWebSocketMessage = useCallback((wsMessage: WhatsAppMessagePayload) => {
    // Guard: Skip messages without valid IDs
    if (!wsMessage.id) {
      return;
    }
    
    // Skip messages that were just sent by this user (tracked in recentlySentMessageIds)
    // This prevents duplicates: user sends via API, message is added, then WebSocket arrives
    if (recentlySentMessageIds.current.has(wsMessage.id)) {
      recentlySentMessageIds.current.delete(wsMessage.id); // Clean up
      return;
    }
    
    // Transform WebSocket payload to Message interface
    const newMessage: Message = {
      id: wsMessage.id,
      direction: wsMessage.direction,
      type: wsMessage.type.toUpperCase() as Message['type'],
      content: wsMessage.content,
      mediaUrl: wsMessage.mediaUrl || undefined,
      metadata: wsMessage.metadata as Message['metadata'],
      status: wsMessage.status,
      createdAt: wsMessage.createdAt,
    };

    // Update messages if this is for the currently selected contact
    if (selectedContactRef.current === wsMessage.contactId) {
      setMessages((prev) => {
        // Check if message already exists (avoid duplicates by ID)
        const exists = prev.some(m => m.id === newMessage.id);
        if (exists) {
          return prev;
        }
        return [...prev, newMessage];
      });

      // Update the 24-hour window if this is an inbound message
      if (wsMessage.direction === 'INBOUND') {
        setMessagesData((prev) => {
          if (!prev) return prev;
          const newExpiresAt = new Date(new Date(wsMessage.createdAt).getTime() + 24 * 60 * 60 * 1000).toISOString();
          return {
            ...prev,
            isWithin24Hours: true,
            lastInboundAt: wsMessage.createdAt,
            windowExpiresAt: newExpiresAt,
          };
        });
      }
    }

    // Update conversation list - move to top and update last message
    setConversations((prev) => {
      const existingIndex = prev.findIndex(c => c.contactId === wsMessage.contactId);
      
      if (existingIndex >= 0) {
        // Update existing conversation
        const updated = [...prev];
        const conversation = { ...updated[existingIndex] };
        conversation.lastMessage = {
          content: wsMessage.content || '',
          type: wsMessage.type.toUpperCase() as Message['type'],
          direction: wsMessage.direction,
          createdAt: wsMessage.createdAt,
          status: wsMessage.status,
        };
        conversation.lastMessageAt = wsMessage.createdAt;
        // Update 24-hour window if inbound
        if (wsMessage.direction === 'INBOUND') {
          conversation.isWithin24Hours = true;
        }
        // Increment unread if not the selected contact and it's an inbound message
        if (selectedContactRef.current !== wsMessage.contactId && wsMessage.direction === 'INBOUND') {
          conversation.unreadCount = (conversation.unreadCount || 0) + 1;
        }
        // Remove from current position
        updated.splice(existingIndex, 1);
        // Add to beginning
        return [conversation, ...updated];
      } else if (wsMessage.direction === 'INBOUND') {
        // New conversation from inbound message
        const newConversation: Conversation = {
          contactId: wsMessage.contactId,
          contactName: wsMessage.contact.name || 'Unknown',
          contactPhone: wsMessage.contact.phoneNumber,
          lastMessage: {
            content: wsMessage.content || '',
            type: wsMessage.type.toUpperCase() as Message['type'],
            direction: wsMessage.direction,
            createdAt: wsMessage.createdAt,
            status: wsMessage.status,
          },
          unreadCount: selectedContactRef.current !== wsMessage.contactId ? 1 : 0,
          lastMessageAt: wsMessage.createdAt,
          isWithin24Hours: true,
        };
        return [newConversation, ...prev];
      }
      
      return prev;
    });
  }, []);

  /**
   * Handle message status updates via WebSocket
   */
  const handleStatusUpdate = useCallback((update: MessageStatusUpdatePayload) => {
    setMessages((prev) => 
      prev.map(msg => 
        msg.id === update.messageId 
          ? { ...msg, status: update.status, errorMessage: update.errorMessage }
          : msg
      )
    );
  }, []);

  /**
   * Handle WebSocket connection changes
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleConnectionChange = useCallback((_connected: boolean) => {
    // Connection status is handled by the useWebSocket hook
  }, []);

  // Initialize WebSocket connection
  const { isConnected: wsConnected, joinConversation, leaveConversation } = useWebSocket({
    onMessage: handleWebSocketMessage,
    onStatusUpdate: handleStatusUpdate,
    onConnectionChange: handleConnectionChange,
    autoConnect: true,
  });

  // Join conversation room when contact is selected
  useEffect(() => {
    if (selectedContact) {
      joinConversation(selectedContact);
    }
    
    return () => {
      if (selectedContact) {
        leaveConversation(selectedContact);
      }
    };
  }, [selectedContact, joinConversation, leaveConversation]);

  // ==================== DATA FETCHING ====================

  const CONVERSATIONS_PER_PAGE = 30;

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      // Always fetch page 1 when refreshing/searching
      const response = await messagesService.getConversations({ 
        search: searchQuery, 
        page: 1, 
        limit: CONVERSATIONS_PER_PAGE 
      });
      
      setConversations(response.items);
      setConversationsPage(1);
      currentPageRef.current = 1; // Reset the ref too
      setTotalConversations(response.meta?.total || response.items.length);
      setHasMoreConversations(response.meta?.hasNextPage || false);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]); // Only depend on searchQuery, not conversationsPage

  // Use ref to prevent multiple simultaneous calls
  const isLoadingMoreRef = useRef(false);
  const currentPageRef = useRef(1);

  // Keep refs in sync with state
  useEffect(() => {
    currentPageRef.current = conversationsPage;
  }, [conversationsPage]);

  const loadMoreConversations = useCallback(async () => {
    // Use ref for immediate check to prevent multiple calls
    if (isLoadingMoreRef.current || !hasMoreConversations) return;
    
    isLoadingMoreRef.current = true;
    setLoadingMoreConversations(true);
    
    try {
      const nextPage = currentPageRef.current + 1;
      const response = await messagesService.getConversations({ 
        search: searchQuery, 
        page: nextPage, 
        limit: CONVERSATIONS_PER_PAGE 
      });
      
      // Append new conversations to existing list
      setConversations(prev => {
        // Filter out any duplicates just in case
        const existingIds = new Set(prev.map(c => c.contactId));
        const newItems = response.items.filter(item => !existingIds.has(item.contactId));
        return [...prev, ...newItems];
      });
      setConversationsPage(nextPage);
      setHasMoreConversations(response.meta?.hasNextPage || false);
    } catch (err) {
      console.error('Failed to load more conversations:', err);
    } finally {
      isLoadingMoreRef.current = false;
      setLoadingMoreConversations(false);
    }
  }, [hasMoreConversations, searchQuery]);

  const fetchMessages = useCallback(async (contactId: string) => {
    try {
      setLoadingMessages(true);
      
      const response = await messagesService.getMessages(contactId, { order: 'desc', page: 1 });
      
      const messagesInOrder = [...response.items].reverse();
      
      // Batch all state updates together including loadingMessages=false
      // so messages render in the DOM immediately (not behind a spinner)
      setMessages(messagesInOrder);
      setMessagesData(response);
      setMessagesPage(1);
      setHasMoreMessages(response.meta.totalPages > 1);
      setLoadingMessages(false);

      // Fire-and-forget: mark as read without blocking the UI
      messagesService.markAsRead(contactId).then(() => {
        setConversations((prev) =>
          prev.map((c) => (c.contactId === contactId ? { ...c, unreadCount: 0 } : c))
        );
      }).catch(() => {});
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError('Failed to load messages');
      setLoadingMessages(false);
    }
  }, []);

  // Load older messages when scrolling up
  const loadMoreMessages = useCallback(async () => {
    if (!selectedContact || loadingMoreMessages || !hasMoreMessages || !messagesData) return;
    
    const nextPage = messagesPage + 1;
    if (nextPage > messagesData.meta.totalPages) {
      setHasMoreMessages(false);
      return;
    }
    
    try {
      setLoadingMoreMessages(true);
      
      // Save current scroll position to maintain it after prepending
      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;
      
      const response = await messagesService.getMessages(selectedContact, { order: 'desc', page: nextPage });
      
      // Reverse and prepend older messages
      const olderMessages = [...response.items].reverse();
      
      // Set flag BEFORE updating messages to prevent scroll-to-bottom effect
      setIsLoadingOlderMessages(true);
      
      setMessages(prev => [...olderMessages, ...prev]);
      setMessagesPage(nextPage);
      setHasMoreMessages(nextPage < messagesData.meta.totalPages);
      
      // Restore scroll position after DOM update
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - previousScrollHeight;
        }
        // Reset flag after scroll position is restored (with delay to ensure effect has run)
        setTimeout(() => {
          setIsLoadingOlderMessages(false);
        }, 100);
      });
    } catch (err) {
      console.error('Failed to load more messages:', err);
      setIsLoadingOlderMessages(false);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [selectedContact, loadingMoreMessages, hasMoreMessages, messagesData, messagesPage]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Track if we've processed the initial contact ID from URL
  const processedInitialContactRef = useRef(false);
  
  // Auto-select contact when page loads (from URL param or first conversation)
  useEffect(() => {
    // Wait for conversations to load
    if (loading) return;
    
    // If contactId is provided in URL and we haven't processed it yet
    if (initialContactId && !processedInitialContactRef.current) {
      processedInitialContactRef.current = true;
      
      // Check if this contact already has a conversation
      const existingConversation = conversations.find((c) => c.contactId === initialContactId);
      
      if (existingConversation) {
        // Contact has existing conversation, just select it
        setSelectedContact(initialContactId);
        setNewChatContact(null);
      } else {
        // Contact has no conversation, set selectedContact immediately to prevent race condition
        // Then fetch contact info and set up new chat
        setSelectedContact(initialContactId);
        
        const setupNewChat = async () => {
          try {
            const contact = await contactsService.getById(initialContactId);
            setNewChatContact({
              id: contact.id,
              name: contact.name || contact.phoneNumber,
              phone: contact.phoneNumber,
            });
            setMessages([]);
            setMessagesData({
              items: [],
              meta: { total: 0, page: 1, limit: 50, totalPages: 0 },
              isWithin24Hours: false, // New conversation, need template
              lastInboundAt: null,
              windowExpiresAt: null,
              contact: {
                id: contact.id,
                name: contact.name || contact.phoneNumber,
                phone: contact.phoneNumber,
              },
            });
          } catch (err) {
            console.error('Failed to fetch contact for new chat:', err);
            // If contact not found, reset and select first conversation
            setSelectedContact(null);
            setNewChatContact(null);
            if (conversations.length > 0) {
              setSelectedContact(conversations[0].contactId);
            }
          }
        };
        setupNewChat();
      }
      // Clear the URL param after using it
      setSearchParams({}, { replace: true });
      return;
    }
    
    // Otherwise, if no contact selected yet and not processing URL param, select first conversation
    if (!selectedContact && !newChatContact && conversations.length > 0 && !initialContactId) {
      setSelectedContact(conversations[0].contactId);
    }
  }, [loading, conversations, selectedContact, newChatContact, initialContactId, setSearchParams]);

  // Handle selecting a contact from the new chat modal
  const handleSelectNewChatContact = (contact: Contact) => {
    setShowContactSelector(false);
    
    // Check if this contact already has a conversation
    const existingConversation = conversations.find((c) => c.contactId === contact.id);
    
    if (existingConversation) {
      // Just select the existing conversation
      setSelectedContact(contact.id);
      setNewChatContact(null);
    } else {
      // Set up for a new conversation
      setNewChatContact({
        id: contact.id,
        name: contact.name || contact.phoneNumber,
        phone: contact.phoneNumber,
      });
      setSelectedContact(contact.id);
      setMessages([]);
      setMessagesData({
        items: [],
        meta: { total: 0, page: 1, limit: 50, totalPages: 0 },
        isWithin24Hours: false, // New conversation, need template
        lastInboundAt: null,
        windowExpiresAt: null,
        contact: {
          id: contact.id,
          name: contact.name || contact.phoneNumber,
          phone: contact.phoneNumber,
        },
      });
    }
  };

  // Handle editing the current contact
  const handleEditContact = async () => {
    if (!selectedContact) return;
    
    try {
      // Fetch the full contact data
      const contactData = await contactsService.getById(selectedContact);
      
      // Fetch groups for the modal
      const groups = await contactsService.getGroups();
      setContactGroups(groups || []);
      
      setEditingContactFull(contactData);
      setShowEditContactModal(true);
    } catch (err) {
      console.error('Failed to fetch contact for editing:', err);
      setError('Failed to load contact details');
    }
  };

  // Handle edit contact success
  const handleEditContactSuccess = async () => {
    setShowEditContactModal(false);
    setEditingContactFull(null);
    
    // Refresh the conversation to get updated contact info
    if (selectedContact) {
      // Fetch updated contact data to refresh the header
      try {
        const updatedContact = await contactsService.getById(selectedContact);
        // Update messagesData with new contact info
        setMessagesData(prev => prev ? {
          ...prev,
          contact: {
            id: updatedContact.id,
            name: updatedContact.name || updatedContact.phoneNumber,
            phone: updatedContact.phoneNumber,
          }
        } : null);
      } catch (err) {
        console.error('Failed to refresh contact data:', err);
      }
    }
    // Refresh conversations list to show updated contact name
    fetchConversations();
  };

  // Track if this is initial load or new message
  const isInitialLoadRef = useRef(true);
  const prevMessagesLengthRef = useRef(0);
  const loadedMessageIdsRef = useRef<Set<string>>(new Set());
  const hasInitialScrollCompletedRef = useRef(false);

  useEffect(() => {
    if (selectedContact && !newChatContact) {
      isInitialLoadRef.current = true;
      hasInitialScrollCompletedRef.current = false;
      loadedMessageIdsRef.current.clear();
      fetchMessages(selectedContact);
    }
  }, [selectedContact, fetchMessages, newChatContact]);

  const scrollToBottom = useCallback((instant = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const doScroll = () => {
      const target = container.scrollHeight - container.clientHeight;
      if (target <= 0) return;
      if (instant) {
        container.scrollTop = target;
      } else {
        try {
          container.scrollTo({ top: target, behavior: 'smooth' });
        } catch {
          container.scrollTop = target;
        }
      }
    };

    doScroll();

    if (instant) {
      setTimeout(doScroll, 100);
      setTimeout(doScroll, 300);
    }
  }, []);

  useEffect(() => {
    // Only scroll when messages are visible (not behind loader)
    if (messages.length === 0 || loadingMessages) {
      prevMessagesLengthRef.current = 0;
      return;
    }

    const isInitialLoad = isInitialLoadRef.current;
    const isNewMessage = messages.length > prevMessagesLengthRef.current && prevMessagesLengthRef.current > 0;
    
    prevMessagesLengthRef.current = messages.length;
    
    if (isLoadingOlderMessages) {
      return;
    }
    
    if (isInitialLoad) {
      isInitialLoadRef.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom(true);
          setShowScrollToBottom(false);
          setTimeout(() => {
            hasInitialScrollCompletedRef.current = true;
          }, 400);
        });
      });
    } else if (isNewMessage) {
      requestAnimationFrame(() => {
        scrollToBottom(false);
        setShowScrollToBottom(false);
      });
    }
  }, [messages, loadingMessages, scrollToBottom, isLoadingOlderMessages]);

  // ==================== SEND MESSAGE ====================

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedContact || sending) return;

    const messageText = messageInput.trim();
    setMessageInput('');
    setSending(true);
    setError(null);

    try {
      const data: SendMessageData = {
        contactId: selectedContact,
        type: 'text',
        text: { body: messageText },
      };

      const newMessage = await messagesService.sendMessage(data);
      
      // Track this message ID to prevent duplicate from WebSocket
      recentlySentMessageIds.current.add(newMessage.id);
      // Clean up after 5 seconds
      setTimeout(() => recentlySentMessageIds.current.delete(newMessage.id), 5000);
      
      // Add message to state, but check for duplicates first
      // (WebSocket may have arrived before this API response)
      setMessages((prev) => {
        const exists = prev.some(m => m.id === newMessage.id);
        if (exists) return prev;
        return [...prev, newMessage];
      });

      // Update conversation
      setConversations((prev) =>
        prev.map((c) =>
          c.contactId === selectedContact
            ? {
                ...c,
                lastMessage: {
                  content: messageText,
                  type: 'text' as MessageType,
                  direction: 'OUTBOUND',
                  createdAt: new Date().toISOString(),
                  status: 'SENT',
                },
                lastMessageAt: new Date().toISOString(),
              }
            : c
        )
      );
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to send message');
      setMessageInput(messageText); // Restore message
    } finally {
      setSending(false);
    }
  };

  // ==================== SEND MEDIA MESSAGE ====================

  const handleSendMedia = async (data: { 
    type: AttachmentType; 
    file?: File;
    caption?: string; 
    location?: { latitude: number; longitude: number; name?: string } 
  }) => {
    if (!selectedContact) return;

    setSending(true);
    setError(null);
    setUploadProgress(0);

    try {
      let messageData: SendMessageData;

      if (data.type === 'location' && data.location) {
        // Location doesn't need file upload
        messageData = {
          contactId: selectedContact,
          type: 'location',
          location: {
            latitude: data.location.latitude,
            longitude: data.location.longitude,
            name: data.location.name,
          },
        };
      } else if (data.file) {
        // Upload file to WhatsApp first to get media ID
        const uploadResult = await messagesService.uploadMedia(
          data.file,
          data.type as 'image' | 'video' | 'document',
          (progress) => setUploadProgress(progress),
        );
        
        const mediaId = uploadResult.mediaId;
        
        // Build message with media ID
        if (data.type === 'image') {
          messageData = {
            contactId: selectedContact,
            type: 'image',
            image: {
              id: mediaId,
              caption: data.caption,
            },
          };
        } else if (data.type === 'video') {
          messageData = {
            contactId: selectedContact,
            type: 'video',
            video: {
              id: mediaId,
              caption: data.caption,
            },
          };
        } else if (data.type === 'document') {
          messageData = {
            contactId: selectedContact,
            type: 'document',
            document: {
              id: mediaId,
              filename: data.file.name,
              caption: data.caption,
            },
          };
        } else {
          throw new Error('Unsupported media type');
        }
      } else {
        throw new Error('No file provided');
      }

      const newMessage = await messagesService.sendMessage(messageData);
      
      // Track this message ID to prevent duplicate from WebSocket
      recentlySentMessageIds.current.add(newMessage.id);
      setTimeout(() => recentlySentMessageIds.current.delete(newMessage.id), 5000);
      
      // Add message to state, but check for duplicates first
      setMessages((prev) => {
        const exists = prev.some(m => m.id === newMessage.id);
        if (exists) return prev;
        return [...prev, newMessage];
      });
      setSelectedMediaType(null);
      setUploadProgress(0);

      // Update conversation
      const contentPreview = data.type === 'location' 
        ? `[Location: ${data.location?.name || 'Shared location'}]`
        : `[${data.type.charAt(0).toUpperCase() + data.type.slice(1)}]`;
      
      setConversations((prev) =>
        prev.map((c) =>
          c.contactId === selectedContact
            ? {
                ...c,
                lastMessage: {
                  content: contentPreview,
                  type: data.type as MessageType,
                  direction: 'OUTBOUND',
                  createdAt: new Date().toISOString(),
                  status: 'SENT',
                },
                lastMessageAt: new Date().toISOString(),
              }
            : c
        )
      );
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to send media');
      setUploadProgress(0);
    } finally {
      setSending(false);
    }
  };

  // Helper function to extract variables from template
  const getTemplateVariables = (template: TemplateItem): string[] => {
    const bodyMatches = template.bodyText?.match(/\{\{(\d+)\}\}/g) || [];
    const headerMatches = (template.headerType === 'TEXT' && template.headerContent)
      ? template.headerContent.match(/\{\{(\d+)\}\}/g) || []
      : [];
    
    const allMatches = [...bodyMatches, ...headerMatches];
    const uniqueVars = Array.from(new Set(allMatches.map(m => m.replace(/[{}]/g, ''))));
    return uniqueVars.sort((a, b) => parseInt(a) - parseInt(b));
  };

  // Auto-populate template variables from contact data
  const autoPopulateVariables = (template: TemplateItem): Record<string, string> => {
    const variables = getTemplateVariables(template);
    const values: Record<string, string> = {};
    
    // Get contact info from messagesData or conversation
    const contactName = messagesData?.contact?.name || selectedConversation?.contactName || '';
    const contactPhone = messagesData?.contact?.phone || selectedConversation?.contactPhone || '';
    
    // Helper to check if text around variable suggests a name
    const isLikelyNameVariable = (varNum: string): boolean => {
      const bodyText = template.bodyText || '';
      const patterns = [
        new RegExp(`(?:hi|hello|dear|hey|name)[^{]*\\{\\{${varNum}\\}\\}`, 'i'),
        new RegExp(`\\{\\{${varNum}\\}\\}[^}]*(?:thank|welcome)`, 'i'),
      ];
      return patterns.some(p => p.test(bodyText));
    };

    // Helper to check sample description for hints
    const getSampleHint = (varNum: string): string | null => {
      const sample = template.variableSamples?.find(s => s.variable === varNum);
      if (!sample?.sample || sample.sample.startsWith('[Variable')) return null;
      
      const sampleLower = sample.sample.toLowerCase();
      // Check if sample looks like a name
      if (/^[a-z]+$/i.test(sample.sample) && sample.sample.length < 20) {
        return contactName || sample.sample;
      }
      // Check for phone-related samples
      if (sampleLower.includes('phone') || /^\+?\d+$/.test(sample.sample)) {
        return contactPhone || sample.sample;
      }
      return sample.sample;
    };

    variables.forEach((varNum, index) => {
      // First, try to get from sample hint
      const sampleHint = getSampleHint(varNum);
      if (sampleHint) {
        values[varNum] = sampleHint;
        return;
      }

      // For first variable, check if it's likely a name
      if (index === 0 && contactName) {
        if (isLikelyNameVariable(varNum)) {
          values[varNum] = contactName;
          return;
        }
        // Default first variable to name if template starts with greeting patterns
        const bodyText = template.bodyText || '';
        if (/^(?:hi|hello|dear|hey|welcome)/i.test(bodyText.trim())) {
          values[varNum] = contactName;
          return;
        }
      }

      // Leave empty for user to fill
      values[varNum] = '';
    });

    return values;
  };

  // Check if template has media header (IMAGE, VIDEO, DOCUMENT)
  const hasMediaHeader = (template: TemplateItem): boolean => {
    const mediaTypes = ['IMAGE', 'VIDEO', 'DOCUMENT'];
    return mediaTypes.includes(template.headerType?.toUpperCase() || '');
  };

  // Check if template has valid media (either a valid media ID or will be handled by backend)
  const hasValidMedia = (template: TemplateItem): boolean => {
    // Check if we have a valid (non-expired) media ID
    if (template.headerMediaId) {
      if (!template.mediaIdExpiresAt) return true; // No expiration set = valid
      return new Date(template.mediaIdExpiresAt) > new Date(); // Check if not expired
    }
    // If no media ID but has headerContent, backend will handle it
    return !!template.headerContent;
  };

  // Check if template needs media input (has media header but no valid media)
  const needsMediaInput = (template: TemplateItem): boolean => {
    if (!hasMediaHeader(template)) return false;
    return !hasValidMedia(template);
  };

  const handleSendTemplate = async (template: TemplateItem) => {
    if (!selectedContact) return;

    // Check if template has variables or needs media input
    const variables = getTemplateVariables(template);
    const requiresMediaInput = needsMediaInput(template);
    
    if (variables.length > 0 || requiresMediaInput) {
      // Auto-populate variables from contact data
      const autoFilledValues = autoPopulateVariables(template);
      setVariableValues(autoFilledValues);
      setHeaderMediaUrl(''); // Empty since we need user input
      setTemplateWithVariables(template);
      setShowTemplateSelector(false);
      return; // Show modal for user to review/edit values
    }

    // Template has no variables and either no media header or has valid media stored
    // Don't pass any media URL - backend will use headerMediaId if available
    await sendTemplateMessage(template, {}, '');
  };

  const sendTemplateMessage = async (template: TemplateItem, variables: Record<string, string>, mediaUrl: string) => {
    if (!selectedContact) return;

    setSending(true);
    setError(null);
    setShowTemplateSelector(false);
    setTemplateWithVariables(null);
    setHeaderMediaUrl('');

    try {
      // Build components array
      const templateVariables = getTemplateVariables(template);
      const templateHasMediaHeader = hasMediaHeader(template);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let components: Array<{ type: string; parameters?: Array<any> }> | undefined;
      
      if (templateVariables.length > 0 || (templateHasMediaHeader && mediaUrl)) {
        const bodyVars = template.bodyText?.match(/\{\{(\d+)\}\}/g) || [];
        const headerVars = (template.headerType === 'TEXT' && template.headerContent)
          ? template.headerContent.match(/\{\{(\d+)\}\}/g) || []
          : [];

        components = [];
        
        // Add header component for media headers (IMAGE, VIDEO, DOCUMENT) if URL provided
        if (templateHasMediaHeader && mediaUrl) {
          const mediaType = template.headerType?.toLowerCase() as 'image' | 'video' | 'document';
          components.push({
            type: 'header',
            parameters: [{
              type: mediaType,
              [mediaType]: { link: mediaUrl },
            }],
          });
        }
        
        // Body parameters
        if (bodyVars.length > 0) {
          const uniqueBodyVars = Array.from(new Set(bodyVars.map(m => m.replace(/[{}]/g, ''))));
          components.push({
            type: 'body',
            parameters: uniqueBodyVars
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map(v => ({ type: 'text', text: variables[v] || `{{${v}}}` })),
          });
        }
        
        // Header parameters (for TEXT headers with variables)
        if (headerVars.length > 0) {
          const uniqueHeaderVars = Array.from(new Set(headerVars.map(m => m.replace(/[{}]/g, ''))));
          components.push({
            type: 'header',
            parameters: uniqueHeaderVars
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map(v => ({ type: 'text', text: variables[v] || `{{${v}}}` })),
          });
        }
      }

      const data: SendMessageData = {
        contactId: selectedContact,
        type: 'template',
        template: { 
          templateId: template.id,
          ...(components && components.length > 0 && { components }),
        },
      };

      const newMessage = await messagesService.sendMessage(data);
      
      // Track this message ID to prevent duplicate from WebSocket
      recentlySentMessageIds.current.add(newMessage.id);
      setTimeout(() => recentlySentMessageIds.current.delete(newMessage.id), 5000);
      
      // Add message to state, but check for duplicates first
      setMessages((prev) => {
        const exists = prev.some(m => m.id === newMessage.id);
        if (exists) return prev;
        return [...prev, newMessage];
      });

      // Check if this is a new conversation
      const isNewConversation = newChatContact !== null;
      
      if (isNewConversation && newChatContact) {
        // Add new conversation to the list
        const newConversation: Conversation = {
          contactId: newChatContact.id,
          contactName: newChatContact.name,
          contactPhone: newChatContact.phone,
          lastMessage: {
            content: `[Template: ${template.name}]`,
            type: 'template' as MessageType,
            direction: 'OUTBOUND',
            createdAt: new Date().toISOString(),
            status: 'SENT',
          },
          unreadCount: 0,
          lastMessageAt: new Date().toISOString(),
          isWithin24Hours: false,
        };
        setConversations((prev) => [newConversation, ...prev]);
        setNewChatContact(null); // Clear the new chat contact
      } else {
        // Update existing conversation
        setConversations((prev) =>
          prev.map((c) =>
            c.contactId === selectedContact
              ? {
                  ...c,
                  lastMessage: {
                    content: `[Template: ${template.name}]`,
                    type: 'template' as MessageType,
                    direction: 'OUTBOUND',
                    createdAt: new Date().toISOString(),
                    status: 'SENT',
                  },
                  lastMessageAt: new Date().toISOString(),
                }
              : c
          )
        );
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to send template');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ==================== RENDER ====================

  // Get selected conversation - either from existing list or from new chat
  const selectedConversation = newChatContact
    ? {
        contactId: newChatContact.id,
        contactName: newChatContact.name,
        contactPhone: newChatContact.phone,
        lastMessage: { content: '', type: 'text' as MessageType, direction: 'OUTBOUND' as const, createdAt: new Date().toISOString(), status: 'PENDING' as const },
        unreadCount: 0,
        lastMessageAt: new Date().toISOString(),
        isWithin24Hours: false,
      }
    : conversations.find((c) => c.contactId === selectedContact);
  const canSendFreeText = messagesData?.isWithin24Hours ?? false;

  return (
    <div className="h-[calc(100vh-120px)] flex bg-(--color-dark-navy) rounded-xl overflow-hidden border border-gray-800">
      {/* Conversations List */}
      <div className="w-80 border-r border-gray-800 flex flex-col bg-[#121723]">
        {/* Header */}
        <div className="p-3 border-b border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-heading font-semibold text-white">Messages</h2>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => setShowContactSelector(true)}
                className="p-1.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-(--color-signal-orange) transition-colors"
                title="New Chat"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => fetchConversations()}
                className="p-1.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-3.5 h-3.5" />}
          />
        </div>

        {/* Conversations */}
        <div 
          ref={conversationsContainerRef}
          className="flex-1 overflow-y-auto"
        >
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 text-(--color-signal-orange) animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-600 mb-3" />
              <p className="text-gray-400 text-sm">No conversations yet</p>
              <p className="text-gray-500 text-xs mt-1 mb-4">Start your first conversation!</p>
              <Button
                size="sm"
                onClick={() => setShowContactSelector(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </Button>
            </div>
          ) : (
            <>
              {/* Conversation count indicator */}
              {totalConversations > 0 && (
                <div className="px-3 py-1.5 text-[10px] text-gray-500 bg-[#0D1117]/50 border-b border-gray-800">
                  Showing {conversations.length} of {totalConversations} conversations
                </div>
              )}
              {conversations.map((conversation, idx) => (
                <ConversationItem
                  key={conversation.contactId || `conv-${idx}`}
                  conversation={conversation}
                  isActive={selectedContact === conversation.contactId}
                  onClick={() => {
                    // Clear newChatContact when selecting an existing conversation
                    setNewChatContact(null);
                    setSelectedContact(conversation.contactId);
                  }}
                />
              ))}
              {/* Loading more indicator */}
              {loadingMoreConversations && (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="w-5 h-5 text-(--color-signal-orange) animate-spin" />
                </div>
              )}
              {/* Load more button (fallback) */}
              {hasMoreConversations && !loadingMoreConversations && (
                <button
                  onClick={loadMoreConversations}
                  className="w-full py-3 text-xs text-gray-400 hover:text-white hover:bg-[#1A2332] transition-colors"
                >
                  Load more conversations...
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedContact && selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b border-gray-800 bg-[#121723] flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-(--color-signal-orange) flex items-center justify-center text-white text-sm font-semibold">
                  {selectedConversation.contactName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-white">{selectedConversation.contactName}</h3>
                  <p className="text-[11px] text-gray-400">{selectedConversation.contactPhone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* WebSocket Connection Indicator */}
                <div 
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] ${
                    wsConnected 
                      ? 'bg-green-500/10 text-green-400' 
                      : 'bg-red-500/10 text-red-400'
                  }`}
                  title={wsConnected ? 'Real-time updates active' : 'Connecting...'}
                >
                  {wsConnected ? (
                    <>
                      <Wifi className="w-3 h-3" />
                      <span>Live</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3" />
                      <span>Offline</span>
                    </>
                  )}
                </div>
                {canSendFreeText ? (
                  /* Show countdown timer when within 24-hour window */
                  <CountdownTimer expiresAt={messagesData?.windowExpiresAt ?? null} />
                ) : (
                  /* Show warning badge when outside 24-hour window */
                  <Badge variant="warning" className="text-[10px]">
                    Use templates to message
                  </Badge>
                )}
                <button className="p-2 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                </button>
                <Dropdown
                  items={[
                    {
                      key: 'edit',
                      label: 'Edit Contact',
                      icon: <Pencil className="w-4 h-4" />,
                      onClick: handleEditContact,
                    },
                  ] as DropdownItem[]}
                  trigger={<MoreVertical className="w-4 h-4" />}
                  triggerClassName="p-2 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors"
                  align="right"
                />
              </div>
            </div>

            {/* Messages Container Wrapper */}
            <div className="flex-1 relative overflow-hidden">
              {/* Scrollable Messages Area */}
              <div 
                ref={messagesContainerRef}
                className="absolute inset-0 overflow-y-auto p-4 bg-[#0D1117]" 
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23374151\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
                onScroll={(e) => {
                  const target = e.currentTarget;
                  if (target.scrollTop < 100 && hasMoreMessages && !loadingMoreMessages && hasInitialScrollCompletedRef.current) {
                    loadMoreMessages();
                  }
                  const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;
                  setShowScrollToBottom(!isNearBottom && messages.length > 0);
                }}
              >
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-(--color-signal-orange) animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-gray-400">No messages yet</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {canSendFreeText ? 'Start the conversation!' : 'Send a template message to start'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Load More Messages Indicator */}
                  {hasMoreMessages && (
                    <div className="flex justify-center py-2 mb-2">
                      {loadingMoreMessages ? (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Loading older messages...</span>
                        </div>
                      ) : (
                        <button
                          onClick={loadMoreMessages}
                          className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-1 rounded-full bg-gray-800 hover:bg-gray-700"
                        >
                          Load older messages
                        </button>
                      )}
                    </div>
                  )}
                  {messages.map((message, index) => {
                    const currentDateKey = getDateKey(message.createdAt);
                    const prevMessage = index > 0 ? messages[index - 1] : null;
                    const prevDateKey = prevMessage ? getDateKey(prevMessage.createdAt) : null;
                    const showDateSeparator = currentDateKey !== prevDateKey;
                    
                    // Check if this message is newly added (not in the initial load)
                    const isNew = !loadedMessageIdsRef.current.has(message.id);
                    // Mark as loaded after first render
                    if (isNew) {
                      loadedMessageIdsRef.current.add(message.id);
                    }
                    
                    return (
                      <div key={message.id || `msg-${index}`}>
                        {showDateSeparator && <DateSeparator date={message.createdAt} />}
                        <MessageBubble message={message} isNew={isNew && !isInitialLoadRef.current} />
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
              </div>
              
              {/* Floating Scroll to Bottom Button - Outside scrollable area */}
              <AnimatePresence>
                {showScrollToBottom && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => {
                      scrollToBottom(false);
                      setShowScrollToBottom(false);
                    }}
                    className="absolute bottom-4 right-6 w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-full shadow-lg flex items-center justify-center transition-colors z-10"
                    title="Scroll to bottom"
                  >
                    <ChevronDown className="w-5 h-5 text-white" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  key="error-message"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="px-4 py-2 bg-red-500/10 border-t border-red-500/30"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </p>
                    <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-800 bg-[#121723] relative">
              <AnimatePresence>
                {showTemplateSelector && (
                  <TemplateSelector
                    key="template-selector"
                    isOpen={showTemplateSelector}
                    onClose={() => setShowTemplateSelector(false)}
                    onSelect={handleSendTemplate}
                  />
                )}
                {showAttachmentMenu && (
                  <AttachmentMenu
                    key="attachment-menu"
                    isOpen={showAttachmentMenu}
                    onClose={() => setShowAttachmentMenu(false)}
                    onSelect={(type) => {
                      setSelectedMediaType(type);
                      setShowAttachmentMenu(false);
                    }}
                  />
                )}
              </AnimatePresence>

              <div className="flex items-end gap-2">
                {/* Attachment Button */}
                <button
                  onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                  className="p-2.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors"
                  disabled={!canSendFreeText}
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                {/* Template Button */}
                <button
                  onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                  className="p-2.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-(--color-signal-orange) transition-colors"
                  title="Send Template"
                >
                  <Layout className="w-5 h-5" />
                </button>

                {/* Message Input */}
                <div className="flex-1">
                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={canSendFreeText ? 'Type a message...' : 'Use templates to send messages'}
                    disabled={!canSendFreeText || sending}
                    className="w-full px-4 py-2.5 bg-[#1A2332] border border-gray-700 rounded-2xl text-white placeholder-gray-500 resize-none focus:outline-none focus:border-(--color-signal-orange) disabled:opacity-50 disabled:cursor-not-allowed"
                    rows={1}
                    style={{ minHeight: '44px', maxHeight: '120px' }}
                  />
                </div>

                {/* Send Button */}
                <Button
                  onClick={handleSendMessage}
                  disabled={(!messageInput.trim() && !showTemplateSelector) || sending || !canSendFreeText}
                  className="rounded-full w-11 h-11 p-0 flex items-center justify-center"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>

              {/* 24-hour window notice */}
              {!canSendFreeText && (
                <p className="text-[11px] text-amber-400/80 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  This contact hasn't replied recently. Send a template to start chatting.
                </p>
              )}
            </div>
          </>
        ) : (
          /* No Chat Selected */
          <div className="flex-1 flex flex-col items-center justify-center bg-[#0D1117] text-center p-8">
            <div className="w-24 h-24 rounded-full bg-(--color-orange-100) flex items-center justify-center mb-6">
              <MessageSquare className="w-12 h-12 text-(--color-signal-orange)" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-white mb-2">AmplifyMsg for Web</h3>
            <p className="text-gray-400 max-w-md mb-6">
              Send and receive WhatsApp messages right from your browser. Select a conversation or start a new chat.
            </p>
            <Button onClick={() => setShowContactSelector(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Start New Chat
            </Button>
          </div>
        )}
      </div>

      {/* Contact Selector Modal */}
      <AnimatePresence>
        {showContactSelector && (
          <ContactSelector
            key="contact-selector"
            isOpen={showContactSelector}
            onClose={() => setShowContactSelector(false)}
            onSelect={handleSelectNewChatContact}
            existingConversationIds={conversations.map((c) => c.contactId)}
          />
        )}
      </AnimatePresence>

      {/* Media Input Modal */}
      <AnimatePresence>
        {selectedMediaType !== null && (
          <MediaInputModal
            key="media-input-modal"
            isOpen={selectedMediaType !== null}
            mediaType={selectedMediaType}
            onClose={() => { setSelectedMediaType(null); setUploadProgress(0); }}
            onSend={handleSendMedia}
            sending={sending}
            uploadProgress={uploadProgress}
          />
        )}
      </AnimatePresence>

      {/* Template Variable Input Modal */}
      <AnimatePresence>
        {templateWithVariables && (
          <motion.div
            key="template-variable-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setTemplateWithVariables(null)}
          >
            <motion.div
              key="template-variable-modal-content"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#121723] rounded-xl border border-gray-800 shadow-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                <div>
                  <h3 className="text-sm font-medium text-white">Review Template Variables</h3>
                  {(() => {
                    const vars = getTemplateVariables(templateWithVariables);
                    const autoFilledCount = vars.filter(v => variableValues[v]?.trim()).length;
                    return autoFilledCount > 0 ? (
                      <p className="text-[10px] text-green-400 mt-0.5">
                        {autoFilledCount} of {vars.length} variable{vars.length > 1 ? 's' : ''} auto-filled from contact
                      </p>
                    ) : null;
                  })()}
                </div>
                <button
                  onClick={() => setTemplateWithVariables(null)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Live Preview with substituted values */}
              <div className="px-4 py-3 border-b border-gray-800 bg-[#0D1117]">
                <p className="text-xs text-gray-400 mb-2">Preview: {templateWithVariables.name}</p>
                <div className="bg-white rounded-lg overflow-hidden text-gray-900 text-sm">
                  {/* Image/Video Header Preview */}
                  {needsMediaInput(templateWithVariables) && headerMediaUrl && (
                    <div className="w-full">
                      {templateWithVariables.headerType === 'IMAGE' && (
                        <img 
                          src={headerMediaUrl} 
                          alt="Header" 
                          className="w-full h-32 object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      {templateWithVariables.headerType === 'VIDEO' && (
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-xs">Video: {headerMediaUrl.split('/').pop()}</span>
                        </div>
                      )}
                      {templateWithVariables.headerType === 'DOCUMENT' && (
                        <div className="w-full h-16 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-xs">Document: {headerMediaUrl.split('/').pop()}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-3">
                    {(() => {
                      let preview = templateWithVariables.bodyText || '';
                      Object.entries(variableValues).forEach(([key, value]) => {
                        if (value.trim()) {
                          preview = preview.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
                        }
                      });
                      return <p className="whitespace-pre-wrap">{preview}</p>;
                    })()}
                  </div>
                </div>
              </div>

              {/* Media Header Input (for IMAGE/VIDEO/DOCUMENT templates without stored content) */}
              {needsMediaInput(templateWithVariables) && (
                <div className="px-4 py-3 border-b border-gray-800">
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    {templateWithVariables.headerType} Header
                    <span className="text-red-400 ml-1">*</span>
                  </label>
                  <Input
                    value={headerMediaUrl}
                    onChange={(e) => setHeaderMediaUrl(e.target.value)}
                    placeholder={`Enter ${templateWithVariables.headerType?.toLowerCase()} URL (e.g., https://example.com/image.jpg)`}
                    className="w-full"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Provide a publicly accessible URL for the {templateWithVariables.headerType?.toLowerCase()}
                  </p>
                </div>
              )}

              {/* Variable Inputs */}
              <div className="px-4 py-4 space-y-3 max-h-[300px] overflow-y-auto">
                {getTemplateVariables(templateWithVariables).map((varNum) => {
                  const sample = templateWithVariables.variableSamples?.find(s => s.variable === varNum);
                  const isAutoFilled = variableValues[varNum]?.trim();
                  return (
                    <div key={varNum}>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Variable {`{{${varNum}}}`}
                        {isAutoFilled && (
                          <span className="text-green-400 ml-1 text-[10px]">(auto-filled)</span>
                        )}
                        {sample?.sample && !sample.sample.startsWith('[Variable') && !isAutoFilled && (
                          <span className="text-gray-500 ml-1">(e.g., {sample.sample})</span>
                        )}
                      </label>
                      <Input
                        value={variableValues[varNum] || ''}
                        onChange={(e) => setVariableValues(prev => ({ ...prev, [varNum]: e.target.value }))}
                        placeholder={`Enter value for {{${varNum}}}`}
                        className={`w-full ${isAutoFilled ? 'border-green-500/30 focus:border-green-500' : ''}`}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-800">
                <Button
                  variant="ghost"
                  onClick={() => setTemplateWithVariables(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => sendTemplateMessage(templateWithVariables, variableValues, headerMediaUrl)}
                  disabled={
                    sending || 
                    Object.values(variableValues).some(v => !v.trim()) ||
                    (needsMediaInput(templateWithVariables) && !headerMediaUrl.trim())
                  }
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Template
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Contact Modal */}
      {showEditContactModal && editingContactFull && (
        <ContactFormModal
          contact={editingContactFull}
          groups={contactGroups}
          onClose={() => {
            setShowEditContactModal(false);
            setEditingContactFull(null);
          }}
          onSuccess={handleEditContactSuccess}
        />
      )}
    </div>
  );
};

export default MessagesPage;
